import React, { useMemo, useRef } from "react";

import { Autocomplete, AutocompleteProps, TextField } from "@mui/material";
import { useCalculatedSelector } from "store";
import { useMeasureParametersSelector } from "store";
import { MeasureType } from "store/types";

import { MeasureGroup } from "./MeasureGroup";

export type MeasuresOption = { measure_type: MeasureType; key: string; label: string };

export interface MeasuresSelectProps {
  disabled?: boolean;
  value: Pick<MeasuresOption, "measure_type" | "key"> | null;
  onChange: (value: { measure_type: MeasureType; key: string } | null, reason?: string) => void;
  sx?: AutocompleteProps<MeasuresOption, boolean, boolean, boolean>["sx"];
}

export const MeasuresSelect: React.FC<MeasuresSelectProps> = ({ value, onChange, disabled = false, sx }) => {
  const popperRef = useRef<any>();
  const inputRef = useRef<HTMLInputElement>(null);

  const calculatedFieldsAll = useCalculatedSelector();
  const measureParameters = useMeasureParametersSelector();

  const options = useMemo(() => {
    return [
      ...measureParameters.map((m) => ({ measure_type: "regular" as MeasureType, label: m.name, key: String(m.id) })),
      ...calculatedFieldsAll.map((cal) => ({ measure_type: "calculated" as MeasureType, label: cal, key: cal }))
    ];
  }, [measureParameters, calculatedFieldsAll]);

  return (
    <Autocomplete<MeasuresOption, false, true>
      sx={{ minWidth: 200, ...sx }}
      disabled={disabled}
      classes={{ listbox: "set-table-columns__listbox" }}
      limitTags={3}
      disableClearable
      options={options}
      groupBy={(option) => option.measure_type}
      value={value ? options.find((o) => o.key === value.key && o.measure_type === value.measure_type) : undefined}
      isOptionEqualToValue={(option, value) => option.key === value.key && option.measure_type === value.measure_type}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onChange={(_, { label, ...rest }, reason) => onChange(rest, reason)}
      getOptionLabel={(option) => option.label}
      renderInput={(params) => <TextField inputRef={inputRef} {...params} label="Parameters" />}
      slotProps={{
        popper: {
          popperRef: popperRef
        }
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <MeasureGroup params={params} popperRef={popperRef} searchValue={inputRef.current?.value || ""} />
        </li>
      )}
    />
  );
};
